import {useEffect, useState} from 'react';
import './App.scss';
import {Alert, AlertTitle, CssBaseline, Snackbar, Slide, SlideProps} from '@mui/material';
import {BrowserRouter as Router, Switch, Route, Redirect} from 'react-router-dom';
import {SplashScreen} from '../components/splash-screen/splash-screen';
import {useAuth} from '../contexts/auth/context';
import {useAppContext} from '../contexts/app/context';
import {ScreenPaths} from '../enums/screen-path';
import {useSnackBar} from '../contexts/snack-bar/context';
import {InviteModal} from '../components/notification-modals/invite-modal/invite-modal';
import {
  PersistInvite,
  PERSIST_INVITE,
  PushNotificationReceived,
  PUSH_NOTIFICATION_RECEIVED,
} from '../events/event-types';
import {useOffice} from '../contexts/office/context';
import {InviteIdentificationType} from '../enums/invite-identification-type';
import {PushNotificationsState} from '../enums/push-notifications-state';
import {useTranslation} from 'react-i18next';
import {translations} from '../i18n/translations';
import {InviteType} from '../backend/enums/invite-type';
import PrivateRoute from '../components/private-route/private-route';
import OfficeScreen from '../screens/private/office/_root/office.screen';
import LoginScreen from '../screens/public/login/login.screen';
import LegacyIntegrationScreen from '../screens/legacy-integration/legacy-integration.screen';
import PublicRoute from '../components/public-route/public-route';
import PublicRouteWithToken from '../components/public-route-with-token/public-route-with-token';
import RecoverPasswordScreen from '../screens/public/password/recover-password/recover-password.screen';
import ValidateInviteScreen from '../screens/public/invite/validate-invite/validate-invite.screen';
import ReplacePasswordScreen from '../screens/public/password/replace-password/replace-password.screen';
import AcceptInviteScreen from '../screens/public/invite/accept-invite/accept-invite.screen';
import CreateCaregiverAccountScreen from '../screens/public/create-caregiver-account/create-caregiver-account.screen';
import PublicRouteWithVoucherCode from '../components/public-route-with-voucher-code/public-route-with-voucher-code';
import eventEmitter from '../events/event-emitter';
import InviteRoute from '../components/invite-route/invite-route';
import DailyCareLogScreen from '../screens/private/daily-care-log/_root/daily-care-log.screen';
import {DailyCareLogContextProvider} from '../contexts/daily-care-log/provider';
import QuickAccessLoginScreen from '../screens/public/quick-access-login/quick-access-login.screen';
import {Videocall} from '../components/videocall/videocall';
import PullToRefresh from 'pulltorefreshjs';
import {QuickAccessManager} from '../managers/quick-access-manager';

function SlideDownTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

function SlideUpTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

export default function Navigation() {
  const appContext = useAppContext();
  const authContext = useAuth();
  const snackBarContext = useSnackBar();
  const officeContext = useOffice();
  const {t} = useTranslation();

  // Hold state
  const [isShowingSplashScreen, setIsShowingSplashScreen] = useState<boolean>(true);
  const [pushNotificationsWarningOpened, setPushNotificationsWarningOpened] =
    useState<boolean>(false);

  // Component mount
  useEffect(() => {
    const bootstrapAsync = async () => {
      // Init app
      appContext.init();

      // Try to restore previous session
      await authContext.restorePreviousSession();

      // Migrate Quick Acess data from local storage to indexedDB.
      await QuickAccessManager.instance.migrateLocalStorageToIndexedDB();
    };

    bootstrapAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle incoming-call launches where the service worker encoded data in the URL.
  // This is needed on Android PWA cold starts where postMessage can be lost before React mounts.
  useEffect(() => {
    const incomingCallParam = new URLSearchParams(window.location.search).get('incomingCall');

    if (!incomingCallParam) {
      return;
    }

    try {
      const incomingCallData = JSON.parse(incomingCallParam) as {
        incomingCallId?: string;
        action?: string;
        appAlreadyOpened?: boolean;
        notification?: any;
      };

      if (incomingCallData.incomingCallId && incomingCallData.notification) {
        officeContext.setIncomingCall({
          callId: incomingCallData.incomingCallId,
          action: incomingCallData.action || '',
          appAlreadyOpened: !!incomingCallData.appAlreadyOpened,
          notification: incomingCallData.notification,
        });

        console.debug('⏹️ Incoming call handled from launch URL.', incomingCallData);
      }
    } catch (error) {
      console.error('❌ Could not handle incoming call from launch URL.', error);
    } finally {
      const url = new URL(window.location.href);
      url.searchParams.delete('incomingCall');
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for any message from our service worker (firebase-messaging-sw.js)
  // Note: We need to listen to invites as fast as possible. Thus, we can't
  // do this in the office screen because it won't work when the website
  // is opened from a closed state.
  useEffect(() => {
    // On service worker message
    const onServiceWorkerMessage = (event: MessageEvent) => {
      // Log
      console.debug('📩 Service worker message in navigation:', event.data);

      // Check if there's a relationship invite to open
      if (event.data && event.data.firebaseSWData) {
        eventEmitter.emit(
          PUSH_NOTIFICATION_RECEIVED,
          event.data.firebaseSWData as PushNotificationReceived
        );

        const unreadNotificationsCount = event.data.firebaseSWData.unreadNotificationsCount;
        const parsedUnreadNotificationsCount =
          typeof unreadNotificationsCount === 'number'
            ? unreadNotificationsCount
            : unreadNotificationsCount !== undefined
              ? parseInt(unreadNotificationsCount, 10)
              : undefined;

        if (
          parsedUnreadNotificationsCount !== undefined &&
          !Number.isNaN(parsedUnreadNotificationsCount)
        ) {
          authContext.updateSessionUnreadNotificationCount(parsedUnreadNotificationsCount);

          // Log
          console.debug(
            '🟢 Service worker message handled in navigation. Notifications count updated.'
          );
        }

        if (event.data.firebaseSWData.relationshipInviteId) {
          // Open the invite
          officeContext.setInvite({
            identificationType: InviteIdentificationType.InviteId,
            identificationValue: event.data.firebaseSWData.relationshipInviteId,
            inviteType: InviteType.Relationship,
          });

          // Log
          console.debug(
            '🟢 Service worker message handled in navigation. Relationship invite opened.'
          );
        } else if (event.data.firebaseSWData.affiliationInviteId) {
          // Open the invite
          officeContext.setInvite({
            identificationType: InviteIdentificationType.InviteId,
            identificationValue: event.data.firebaseSWData.affiliationInviteId,
            inviteType: InviteType.Affiliation,
          });

          // Log
          console.debug(
            '🟢 Service worker message handled in navigation. Affiliation invite opened.'
          );
        } else if (event.data.firebaseSWData.incomingCallId) {
          // Handle Incoming call
          officeContext.setIncomingCall({
            callId: event.data.firebaseSWData.incomingCallId,
            action: event.data.firebaseSWData.action,
            appAlreadyOpened: event.data.firebaseSWData.appAlreadyOpened,
            notification: event.data.firebaseSWData.notification,
          });

          // Log
          console.debug(
            '⏹️ Service worker message handled in navigation. Incoming call handled.',
            event.data.firebaseSWData
          );
        } else if (event.data.firebaseSWData.occurrenceId) {
          console.debug('⏹️ Service worker message handled in navigation. New occurrence handled.');
          officeContext.setOccurrence({
            id: event.data.firebaseSWData.occurrenceId,
            entityId: event.data.firebaseSWData.entityId,
            date: event.data.firebaseSWData.registeredAt,
            appAlreadyOpened: event.data.firebaseSWData.appAlreadyOpened,
          });
        } else {
          // Log
          console.debug('⏹️ Service worker message not handled in navigation.');
        }
      } else {
        // Log
        console.debug('⏹️ Service worker message not handled in navigation.');
      }
    };

    // Listen for invite events
    const onPersistInvite = (data: PersistInvite) => {
      // Set  invite
      officeContext.setInvite(data);
    };
    // Listen
    navigator.serviceWorker?.addEventListener('message', onServiceWorkerMessage);
    eventEmitter.on(PERSIST_INVITE, onPersistInvite);

    // Clean up
    return () => {
      // Unsubscribe
      navigator.serviceWorker?.removeEventListener('message', onServiceWorkerMessage);
      eventEmitter.off(PERSIST_INVITE, onPersistInvite);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authContext.state.isRestoringSession) {
      const isPWA =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        window.matchMedia('(display-mode: minimal-ui)').matches;
      const isSafari =
        /Safari/i.test(navigator.userAgent) && !/Chrome|Chromium/i.test(navigator.userAgent);

      if (
        isSafari &&
        isPWA &&
        (document.location.pathname.includes('office') ||
          document.location.pathname.includes('daily-care-log'))
      ) {
        PullToRefresh.init({
          mainElement: 'body',
          instructionsRefreshing: t(translations.home.refreshing),
          instructionsReleaseToRefresh: t(translations.home.release_to_refresh),
          instructionsPullToRefresh: t(translations.home.pull_to_refresh),
          onRefresh() {
            window.location.reload();
          },
        });
      }

      return () => {
        PullToRefresh.destroyAll();
      };
    }
  }, [authContext.state.isRestoringSession, t]);

  // Check if we need to show the push notifications warning
  useEffect(() => {
    if (
      authContext.state.pushNotificationsState === PushNotificationsState.UnsubscribedError ||
      authContext.state.pushNotificationsState ===
        PushNotificationsState.UnsubscribedNotSupported ||
      authContext.state.pushNotificationsState ===
        PushNotificationsState.UnsubscribedPermissionsNotGranted
    ) {
      setPushNotificationsWarningOpened(true);
    }
  }, [authContext.state.pushNotificationsState]);

  // Close push notifications warning
  const handleClosePushNotificationsWarning = () => setPushNotificationsWarningOpened(false);

  return (
    <>
      {authContext.state.isRestoringSession ? undefined : (
        <>
          {/* Material UI Basic CSS */}
          <CssBaseline />

          {/* Main Router */}
          <Router
            getUserConfirmation={() => {
              /* Empty callback to block the default browser prompt */
              /* Read about it in https://medium.com/nerd-for-tech/custom-react-router-prompt-d325538b4d2b */
            }}
          >
            {/*
                            A <Switch> looks through all its children <Route>
                            elements and renders the first one whose path
                            matches the current URL. Use a <Switch> any time
                            you have multiple routes, but you want only one
                            of them to render at a time
                        */}
            <Switch>
              {/** Private Routes */}
              <PrivateRoute path={ScreenPaths.Office}>
                <OfficeScreen />
              </PrivateRoute>
              <PrivateRoute path={ScreenPaths.DailyCareLog}>
                <DailyCareLogContextProvider>
                  <DailyCareLogScreen />
                </DailyCareLogContextProvider>
              </PrivateRoute>

              {/** Public Routes */}
              <PublicRoute path={ScreenPaths.Login}>
                <LoginScreen title={translations.login.welcome} showIllustration />
              </PublicRoute>
              <PublicRoute path={ScreenPaths.LoginDailyCareLog}>
                <LoginScreen title={translations.login.welcome_daily_care_log} />
              </PublicRoute>
              <PublicRoute path={ScreenPaths.LoginQuickAccess}>
                <QuickAccessLoginScreen />
              </PublicRoute>
              <PublicRoute path={ScreenPaths.RecoverPassword}>
                <RecoverPasswordScreen />
              </PublicRoute>
              <PublicRouteWithToken path={ScreenPaths.ReplacePassword}>
                <ReplacePasswordScreen />
              </PublicRouteWithToken>
              <PublicRoute path={ScreenPaths.ValidateInvite}>
                <ValidateInviteScreen />
              </PublicRoute>
              <PublicRouteWithVoucherCode path={ScreenPaths.CreateInformalCaregiverAccount}>
                <CreateCaregiverAccountScreen type="relationship" />
              </PublicRouteWithVoucherCode>
              <PublicRouteWithVoucherCode path={ScreenPaths.CreateProfessionalCaregiverAccount}>
                <CreateCaregiverAccountScreen type="affiliation" />
              </PublicRouteWithVoucherCode>

              {/** Relationship Invite Route (depending on the user being logged in or not, different things can happen) */}
              <InviteRoute path={ScreenPaths.AcceptRelationshipInvite}>
                <AcceptInviteScreen type="relationship" />
              </InviteRoute>

              {/** Affiliation Invite Route (depending on the user being logged in or not, different things can happen) */}
              <InviteRoute path={ScreenPaths.AcceptAffiliationInvite}>
                <AcceptInviteScreen type="affiliation" />
              </InviteRoute>

              {/** Legacy Integration (for the old back office) */}
              <Route path={ScreenPaths.LegacyIntegration}>
                <LegacyIntegrationScreen />
              </Route>

              {/** Redirects */}
              <Route exact path={ScreenPaths.Root}>
                <Redirect to={ScreenPaths.Office} />
              </Route>
              <Redirect to={ScreenPaths.Office} />
            </Switch>
          </Router>
        </>
      )}

      {(document.location.pathname.includes('office') ||
        document.location.pathname.includes('daily-care-log')) && <Videocall />}

      {/* Splash screen */}
      {isShowingSplashScreen ? (
        <div
          className={`${authContext.state.isRestoringSession ? 'splashscreen-shown' : 'splashscreen-hidden'}`}
          onTransitionEnd={() => setIsShowingSplashScreen(false)}
        >
          <SplashScreen />
        </div>
      ) : undefined}

      {/* Invites */}
      {authContext.state.session &&
        officeContext.state.invite &&
        !authContext.state.isRestoringSession &&
        !isShowingSplashScreen && (
          <InviteModal
            opened={true}
            handleClose={() => officeContext.clearInvite()}
            identificationType={officeContext.state.invite.identificationType}
            identificationValue={officeContext.state.invite.identificationValue}
          />
        )}

      {/* Public toast messages (used for messages outside the office screen) */}
      <Snackbar
        TransitionComponent={SlideDownTransition}
        anchorOrigin={{vertical: 'top', horizontal: 'left'}}
        open={snackBarContext.state.publicToastNotification.open}
        autoHideDuration={snackBarContext.state.publicToastNotification.autoHideDuration}
        onClose={snackBarContext.resetPublicToastNotification}
      >
        <Alert
          variant="filled"
          sx={{width: '100%'}}
          onClose={snackBarContext.resetPublicToastNotification}
          severity={snackBarContext.state.publicToastNotification.severity}
        >
          {snackBarContext.state.publicToastNotification.title && (
            <AlertTitle>{snackBarContext.state.publicToastNotification.title}</AlertTitle>
          )}
          {snackBarContext.state.publicToastNotification.message}
        </Alert>
      </Snackbar>

      {/* Push notifications warning */}
      <Snackbar
        TransitionComponent={SlideUpTransition}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        open={pushNotificationsWarningOpened}
        autoHideDuration={10000}
        onClose={handleClosePushNotificationsWarning}
      >
        <Alert variant="filled" severity="warning" onClose={handleClosePushNotificationsWarning}>
          <>
            <AlertTitle>
              {t(translations.push_notifications.warnings.unsubscribed_generic_warning_title)}
            </AlertTitle>
            {authContext.state.pushNotificationsState ===
              PushNotificationsState.UnsubscribedError &&
              t(translations.push_notifications.warnings.unsubscribed_error_warning_message)}
            {authContext.state.pushNotificationsState ===
              PushNotificationsState.UnsubscribedNotSupported &&
              t(
                translations.push_notifications.warnings.unsubscribed_not_supported_warning_message
              )}
            {authContext.state.pushNotificationsState ===
              PushNotificationsState.UnsubscribedPermissionsNotGranted &&
              t(
                translations.push_notifications.warnings
                  .unsubscribed_permissions_denied_warning_message
              )}
          </>
        </Alert>
      </Snackbar>
    </>
  );
}
